'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Country {
  id: string;
  name: string;
  flag: string;
  flagAlt: string;
  currency: string;
  currencySymbol: string;
  avgTuition: number;
  avgLiving: number;
  visaProcessing: string;
  workRights: string;
  language: string;
}

interface ComparisonToolProps {
  countries: Country[];
}

export default function ComparisonTool({ countries }: ComparisonToolProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const toggleCountry = (countryId: string) => {
    setSelectedCountries(prev => {
      if (prev.includes(countryId)) {
        return prev.filter(id => id !== countryId);
      }
      if (prev.length < 3) {
        return [...prev, countryId];
      }
      return prev;
    });
  };

  const selectedCountryData = countries.filter(c => selectedCountries.includes(c.id));

  if (!isHydrated) {
    return (
      <div className="fixed bottom-8 right-8 z-40">
        <div className="w-16 h-16 bg-primary rounded-full animate-pulse" />
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-8 right-8 z-40 w-16 h-16 bg-primary text-primary-foreground rounded-full shadow-elevated hover:bg-primary/90 transition-all duration-300 flex items-center justify-center group"
        aria-label="Open comparison tool"
      >
        <Icon name="ScaleIcon" size={24} className="text-current" />
        {selectedCountries.length > 0 && (
          <span className="absolute -top-2 -right-2 w-6 h-6 bg-conversion text-conversion-foreground rounded-full text-xs font-bold flex items-center justify-center">
            {selectedCountries.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-4">
          <div className="bg-card rounded-t-2xl md:rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Compare Destinations</h2>
                <p className="text-sm text-muted-foreground mt-1">Select up to 3 countries to compare</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Close comparison tool"
              >
                <Icon name="XMarkIcon" size={24} className="text-foreground" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {selectedCountries.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="GlobeAltIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Select countries from the list below to start comparing</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-4 font-semibold text-foreground">Criteria</th>
                        {selectedCountryData.map(country => (
                          <th key={country.id} className="p-4 text-center">
                            <div className="flex flex-col items-center space-y-2">
                              <AppImage
                                src={country.flag}
                                alt={country.flagAlt}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                              <span className="font-semibold text-foreground">{country.name}</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-border">
                        <td className="p-4 font-medium text-foreground">Currency</td>
                        {selectedCountryData.map(country => (
                          <td key={country.id} className="p-4 text-center text-foreground">
                            {country.currencySymbol} {country.currency}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-border">
                        <td className="p-4 font-medium text-foreground">Avg. Tuition/Year</td>
                        {selectedCountryData.map(country => (
                          <td key={country.id} className="p-4 text-center text-foreground">
                            {country.currencySymbol}{country.avgTuition.toLocaleString()}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-border">
                        <td className="p-4 font-medium text-foreground">Avg. Living Cost/Year</td>
                        {selectedCountryData.map(country => (
                          <td key={country.id} className="p-4 text-center text-foreground">
                            {country.currencySymbol}{country.avgLiving.toLocaleString()}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-border">
                        <td className="p-4 font-medium text-foreground">Visa Processing</td>
                        {selectedCountryData.map(country => (
                          <td key={country.id} className="p-4 text-center text-foreground">
                            {country.visaProcessing}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-border">
                        <td className="p-4 font-medium text-foreground">Work Rights</td>
                        {selectedCountryData.map(country => (
                          <td key={country.id} className="p-4 text-center text-foreground">
                            {country.workRights}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="p-4 font-medium text-foreground">Primary Language</td>
                        {selectedCountryData.map(country => (
                          <td key={country.id} className="p-4 text-center text-foreground">
                            {country.language}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-8">
                <h3 className="text-lg font-semibold text-foreground mb-4">Select Countries</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {countries.map(country => (
                    <button
                      key={country.id}
                      onClick={() => toggleCountry(country.id)}
                      disabled={!selectedCountries.includes(country.id) && selectedCountries.length >= 3}
                      className={`p-4 rounded-lg border-2 transition-all duration-300 text-left ${
                        selectedCountries.includes(country.id)
                          ? 'border-primary bg-primary/10' :'border-border hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <AppImage
                          src={country.flag}
                          alt={country.flagAlt}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <span className="font-medium text-foreground text-sm">{country.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}